import { FormControl, FormControlLabel, TextField } from "@mui/material";

import MyDialog from "~/mui/MyDialog";
import { useTrans } from "~/hooks/useTrans";
import { myUseState } from "~/hooks/myUseState";
import { IAdminProject, IAdminChangeProjectBillingConsumption } from "~/providers/admin-interfaces";
import { IClientProject, IClientChangeProjectBillingConsumption } from "~/providers/client-interfaces";
import { MySwitch } from "~/mui/MySwitch";
import { parseNumber, checkForErrorsState } from "~/utils/utils";
import { apiUpdateProjectBillingConsumption } from "~/providers/api";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IProjectState extends Pick<IClientProject, "billedConsumption" | "softConsumptionLimit" | "totalConsumptionLimit" | "lastUpdate"> {}

// chyby formulare
type TStateProjectErrors = {[key in keyof Pick<IProjectState, "totalConsumptionLimit" | "softConsumptionLimit">]: string; };

interface IPaidConsumptionModal {
	isInternal: boolean;
	projectData: IClientProject | IAdminProject;
	onClose?: () => void;
	onSave?: (projectData: IClientChangeProjectBillingConsumption | IAdminChangeProjectBillingConsumption) => void;
	onChange?: (isChanged: boolean) => void;
}

export default function PaidConsumptionModal({
	isInternal = false,
	projectData = null,
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IPaidConsumptionModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateProject, updateState: updateStateProject } = myUseState<IProjectState>({
		billedConsumption: projectData.billedConsumption,
		softConsumptionLimit: projectData.softConsumptionLimit,
		totalConsumptionLimit: projectData.totalConsumptionLimit,
		lastUpdate: projectData.lastUpdate,
	});
	const { state: stateProjectError, setState: setStateProjectError } = myUseState<TStateProjectErrors>({
		softConsumptionLimit: "",
		totalConsumptionLimit: "",
	});

	function getWasChanged(newState: Partial<IProjectState>) {
		const newStateObj = {
			...stateProject,
			...newState,
		};

		return newStateObj.billedConsumption !== projectData.billedConsumption
			|| newStateObj.softConsumptionLimit !== projectData.softConsumptionLimit
			|| newStateObj.totalConsumptionLimit !== projectData.totalConsumptionLimit;
	}

	function updateStateProjectWithChange(newState: Partial<IProjectState>) {
		const newStateChanged: Partial<IProjectState> = {
			...newState,
			..."billedConsumption" in newState && newState.billedConsumption === false
				? {
					totalConsumptionLimit: null,
				}
				: {},
		};
		const wasChanged = getWasChanged(newStateChanged);

		updateStateProject(newStateChanged);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	function setProjectNumber(name: keyof IProjectState, value: string) {
		const newValue = value
			? parseNumber(value)
			: null;

		updateStateProjectWithChange({ [name]: newValue });
	}

	async function dialogOnSave() {
		const errorData = checkForErrorsState<IProjectState, TStateProjectErrors>(stateProject, {
			...stateProject.billedConsumption
				? {
					totalConsumptionLimit: { check: "number-bigint", msg: trans("biginit.error") },
					...stateProject.softConsumptionLimit === null
						? {}
						: {
							softConsumptionLimit: [{
								check: "number-bigint", msg: trans("biginit.error"),
							}, {
								check: "custom",
								msg: trans("createProject.error.softLargerThenTotal"),
								custom: () => stateProject.softConsumptionLimit >= stateProject.totalConsumptionLimit,
							}],
						},
				}
				: {
					...stateProject.softConsumptionLimit === null
						? {}
						: {
							softConsumptionLimit: [
								{ check: "number-bigint", msg: trans("biginit.error") },
							],
						},
				},
		});

		setStateProjectError({
			softConsumptionLimit: "",
			...errorData.errorsState,
		});

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const sendData: IProjectState = {
			...stateProject,
		};

		// odstranime z dat pro vsechny pripady
		if (!sendData.billedConsumption) {
			sendData.totalConsumptionLimit = null;
		}

		const outputData = await apiUpdateProjectBillingConsumption(isInternal ? "admin" : "client", projectData.id, sendData, lang);

		if (outputData.noErrors) {
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("paidConsumptionModal.title")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		cancelCaption={ trans("cancel") } saveCaption={ trans("saveChanges")} disableSave={!state.wasChanged} confirmClose={state.wasChanged} minWidth="400px" className="paidConsumptionModal">
		<div className="paidConsumptionModal__allowPaid">
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateProject.billedConsumption} onChange={event => updateStateProjectWithChange({ billedConsumption: event.target.checked })} />}
				label={trans("paidConsumptionModal.allowPaidConsumption")}
			/>
		</div>
		<FormControl className="paidConsumptionModal__formControl">
			{ stateProject.billedConsumption && <TextField
				label={trans("paidConsumptionModal.totalConsumptionLimit")}
				value={stateProject.totalConsumptionLimit === null ? "" : stateProject.totalConsumptionLimit}
				onChange={event => setProjectNumber("totalConsumptionLimit", event.target.value)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateProjectError.totalConsumptionLimit !== ""}
				helperText={stateProjectError.totalConsumptionLimit || trans("paidConsumptionModal.freeCredits")}
				size="small"
			/> }
			<TextField
				label={trans("paidConsumptionModal.softConsumptionLimit")}
				value={stateProject.softConsumptionLimit === null ? "" : stateProject.softConsumptionLimit}
				onChange={event => setProjectNumber("softConsumptionLimit", event.target.value)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateProjectError.softConsumptionLimit !== ""}
				helperText={stateProjectError.softConsumptionLimit || trans("paidConsumptionModal.freeCredits")}
				size="small"
			/>
		</FormControl>
	</MyDialog>;
}
